import React, { useEffect, useState } from "react";
import { useIsFocused, useNavigation, useRoute } from "@react-navigation/native";
import { useSelector } from "react-redux";
import i18n from "utils/i18n";
import Translate from "utils/translate";
import _ from "lodash";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import ThemedBlock from "components/atoms/ThemedBlock";
import LoadingComponent from "components/atoms/Loading";
import Form, { createTranslationField } from "components/molecules/Form";
import { FormFieldsConstants, FormStatus } from 'utils/constants/form-constants';
import GraphqlBuilder from "utils/request/graphql/builder";
import { tableConstants } from "utils/constants/constants";
import { dataConstants } from "services/redux/data/constants";
import MutateTranslation from 'utils/mutate/translation'
import HelperString from 'utils/helper-string'
import QueryManagementJobtitle from 'graphql/queries/management/QueryManagementJobtitle'
import MutationManagementJobtitle from 'graphql/mutations/management/MutationManagementJobtitle'
import HelperManagementRights from "utils/helper-management-rights";
import { managementRightsConstants } from "utils/constants/management-right-constants";
import { AppRoutes } from 'navigations/app-routes'
import ObjectJobtitles from 'utils/objects/jobtitles'

const ManagementJobtitleScene = () => {
	
	const navigation = useNavigation();
	const route = useRoute();
	const isFocused = useIsFocused();
	
	const _languages = useSelector( state => {
		return state.data[dataConstants.LANGUAGES];
	} )
	
	const [ loading, setLoading ] = useState( true )
	const [ jobtitle, setJobtitle ] = useState( {} )
	
	const jobtitleGuid = _.get( route.params, 'jobtitleGuid', null );
	
	const fetchJobtitle = () => {
		if( jobtitleGuid !== null ) {
			new GraphqlBuilder()
				.addQuery( 'query', QueryManagementJobtitle, {
					guid: jobtitleGuid
				})
				.setResultPath( ['query', 'jobtitle'], {} )
				.setOnSuccess( result => {
					setJobtitle( result )
					setLoading( false )
				})
				.setOnError( setLoading.bind( this, false ) )
				.execute()
			
			return;
		}
		
		setJobtitle( ObjectJobtitles.defaults )
		setLoading( false )
	}
	
	const saveForm = ( values, { callback } = {} ) => {
		new GraphqlBuilder()
			.addQuery( 'mutation', MutationManagementJobtitle, values )
			.setResultPath( ['mutation', 'jobtitle'], {} )
			.setOnStart( () => {
				callback( FormStatus.LOADING )
			})
			.setOnSuccess( ( result ) => {
				MutateTranslation.mutate( tableConstants.JOBTITLES, result.guid, values, {
					callbackSuccess: ( t ) => {
						callback( FormStatus.SUCCESS )
						
						navigation.replace( AppRoutes.MANAGEMENT_JOBTITLES_JOBTITLES_VIEW, {
							jobtitleGuid: result.guid
						});
					},
					callbackError: () => {
						callback( FormStatus.ERROR )
					}
				} )
			})
			.setOnError( ( result ) => {
				callback( FormStatus.ERROR )
			})
			.execute()
	}
	
	useEffect( () => {
		return fetchJobtitle();
	}, [ isFocused, jobtitleGuid ] )
	
	let content = <LoadingComponent />
	if( !loading ) {
		const formFields = [
			{
				name: 'name',
				label: i18n.t('management:form.name.system_name'),
				type: FormFieldsConstants.TEXT,
			},
			{
				name: 'active',
				label: i18n.t('management:form.active.title'),
				type: FormFieldsConstants.SELECT_ACTIVE
			}
		];
		
		// Add the translations
		if( _.size( _languages ) > 0 ) {
			formFields.push( {
				type: FormFieldsConstants.DIVIDER
			} );
			
			// Add the translation fields
			_.forEach( _languages, ( language ) => {
				const { name, formField } = createTranslationField( 'name', language );
				
				formFields.push( formField );
				
				const translation = Translate.formField( jobtitle, 'title', language );
				if( !_.isEmpty( jobtitle ) && translation !== null ) {
					jobtitle[ name ] = translation;
				}
			});
		}
		
		content = (
			<ThemedBlock
				header={ i18n.t('management:form_block.title_default') }
				shadow
				mediumPadding>
				
				{/* Form */}
				<Form
					key={ HelperString.hash( 'form', jobtitle ) }
					values={ jobtitle }
					fields={ formFields }
					onSubmit={ saveForm }
					disabled={ !HelperManagementRights.hasEditRight( managementRightsConstants.EDIT.JOBTITLES ) }
				/>
			</ThemedBlock>
		)
	}
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>

			<ThemedLayout
				title={ i18n.t('management:pages.jobtitles.jobtitle.title') }
				topNavigation={ true }
				showBackButton={ true }>
				
				<ThemedBlock transparent/>
				
				{ content }
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	);
}

export default ManagementJobtitleScene
